import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/Layout";
import OutstandingNotes from "@/components/OutstandingNotes";
import CatchUpBanner from "@/components/CatchUpBanner";
import { Clock, MapPin, ChevronRight, AlertTriangle, ShieldCheck } from "lucide-react";
import { useCalendarEvents, classifyDate } from "@/lib/calendarEvents";

function startOfWeek() { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d; }

export default function MyJobs() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [swms, setSwms] = useState(null);
  const [tab, setTab] = useState("today");
  const events = useCalendarEvents();

  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data)); api.get("/swms").then((r) => setSwms(r.data)).catch(() => {}); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayInfo = classifyDate(events, today);
  const weekStart = startOfWeek().toISOString().slice(0, 10);
  const weekEnd = (() => { const d = startOfWeek(); d.setDate(d.getDate() + 6); return d.toISOString().slice(0, 10); })();

  const workerDays = (j) => (j.worker_days && j.worker_days.length ? j.worker_days : (j.appointment_date ? [j.appointment_date] : []));
  const dayTime = (j, d) => { const sd = (j.schedule_days || []).find((s) => (s.date || "").slice(0, 10) === d); return (sd && sd.start_time) || j.start_time || "—"; };
  const dayStatus = (j, d) => {
    const done = (j.dockets || []).some((dk) => !dk.archived && (dk.visit_date || "").slice(0, 10) === d);
    if (done) return "completed";
    const pending = (j.pending_dockets || []).some((p) => (p.visit_date || "").slice(0, 10) === d);
    const legacyAwaiting = !(j.pending_dockets || []).length && j.status === "awaiting_signature" && d === (j.awaiting_signature_date || (j.appointment_date || "").slice(0, 10));
    if (pending || legacyAwaiting) return "awaiting_signature";
    if (j.status === "in_progress" && (j.appointment_date || "").slice(0, 10) === d) return "in_progress";
    return "scheduled";
  };
  const instances = [];
  jobs.forEach((j) => workerDays(j).forEach((d) => instances.push({ j, date: d })));

  const filtered = (tab === "completed"
    ? jobs.filter((j) => j.status === "completed").map((j) => ({ j, date: j.appointment_date }))
    : instances.filter(({ date }) => (tab === "today" ? date === today : date >= weekStart && date <= weekEnd))
  ).sort((a, b) => ((a.date || "") + dayTime(a.j, a.date)).localeCompare((b.date || "") + dayTime(b.j, b.date)));

  const counts = {
    today: instances.filter(({ date }) => date === today).length,
    week: instances.filter(({ date }) => date >= weekStart && date <= weekEnd).length,
    completed: jobs.filter((j) => j.status === "completed").length,
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div>
        <h1 className="text-3xl font-heading font-bold text-navy">My Jobs</h1>
        <p className="text-slate-500 mt-1">{new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}</p>
      </div>

      <OutstandingNotes jobPath={(id) => `/jobs/${id}`} />
      <CatchUpBanner jobPath={(id) => `/jobs/${id}`} />

      {swms?.has_file && (
        <a href={`${API}/swms/file?auth=${localStorage.getItem("ts_token")}`} target="_blank" rel="noreferrer" data-testid="myjobs-swms-link"
           className="flex items-center justify-between gap-3 bg-navy text-white rounded-xl px-4 py-3 shadow-sm">
          <span className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="w-5 h-5 text-emerald-400" />Safe Work Method Statement (v{swms.version})</span>
          <span className="text-xs bg-white/15 px-2 py-1 rounded-full">View SWMS</span>
        </a>
      )}

      <div className="flex gap-2 bg-slate-100 p-1 rounded-xl">
        {[["today", `Today (${counts.today})`], ["week", `This Week (${counts.week})`], ["completed", "Completed"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab === k ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "today" && (todayInfo.holiday || todayInfo.rdo) && (
        <div data-testid="tech-day-event-banner" className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${todayInfo.both ? "bg-red-50 border-red-200 text-red-800" : todayInfo.holiday ? "bg-yellow-50 border-yellow-200 text-yellow-800" : "bg-orange-50 border-orange-200 text-orange-800"}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            {todayInfo.holiday && <p><span className="font-semibold">Public Holiday</span> — {todayInfo.holiday}.</p>}
            {todayInfo.rdo && <p><span className="font-semibold">Construction Industry RDO</span> — many sites may be closed. Confirm site access before attending.</p>}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filtered.length === 0 && <p className="text-slate-400 text-center py-12">No jobs to show.</p>}
        {filtered.map(({ j, date }) => (
          <div key={`${j.id}-${date}`} data-testid={`myjob-card-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)}
            className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex items-center gap-4">
            <div className="flex flex-col items-center justify-center bg-navy text-white rounded-xl px-3 py-2 w-20 shrink-0">
              <Clock className="w-4 h-4 mb-1" />
              <span className="text-sm font-semibold">{dayTime(j, date)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-semibold text-navy truncate">{j.customer_name}</p>
              <p className="text-sm text-slate-500 truncate flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{j.site_address || "No address"}</p>
              <div className="mt-2"><StatusPill status={tab === "completed" ? j.status : dayStatus(j, date)} /></div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
